import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion"; // Import motion
import { MenuSectionProps } from "./types";
import { MenuItem } from "./MenuItem";
import { ChevronDown, ChevronUp, XCircle } from "lucide-react"; // Import chevrons and XCircle

export const MenuSection: React.FC<MenuSectionProps> = ({
  title,
  items,
  selectedIndex,
  startIndex,
  onAdd,
  onDragStart,
  onDrag,
  onDragEnd,
  onToggleFavorite, // Added prop
  favorites, // Added prop
  onClearFavorites, // Added prop
}) => {
  const [isHeaderHovered, setIsHeaderHovered] = useState(false); // Track header hover
  const storageKey = `leftPanelSectionCollapsed_${title.replace(/\s+/g, '_')}`; // Make key safer
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load initial state from localStorage, default to false (expanded)
    // For FAVORITES, default to expanded if it has items, otherwise collapsed
    if (title === "FAVORITES") {
      const savedState = localStorage.getItem(storageKey);
      return savedState !== null ? savedState === 'true' : items.length === 0;
    }
    return localStorage.getItem(storageKey) === 'true';
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(storageKey, String(isCollapsed));
  }, [isCollapsed, storageKey]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  // Animation variants for the content (height only, no fade)
  const contentVariants = {
    open: {
      height: "auto",
      marginBottom: "6px",
      transition: { duration: 0.2, ease: "easeInOut" },

    },
    closed: {
      height: 0,
      marginTop: "0px", // Remove margin when closed
      overflow: "hidden", // Keep overflow hidden during transition
      transition: { duration: 0.2, ease: "easeInOut" },

    },
  };

  // Don't render the section if it's not the FAVORITES section and has no items
  if (title !== "FAVORITES" && items.length === 0) return null;
  // Don't render FAVORITES section if it has no items and is collapsed (to avoid empty collapsed header)
  if (title === "FAVORITES" && items.length === 0 && isCollapsed) return null;


  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      {/* Clickable Header with Hover */}
      <div
        className="px-4 pt-3 pb-3 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 group" // Added group for hover effect
        onClick={toggleCollapse}
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
      >
        <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</h3>
        <div className="flex items-center space-x-3">
          {/* Clear Button - Show only for FAVORITES on hover */}
          {title === "FAVORITES" && onClearFavorites && favorites.length > 0 && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: isHeaderHovered ? 1 : 0 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent toggling collapse
                onClearFavorites();
              }}
              className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400"
              title="Clear Favorites"
            >
              <XCircle size={14} />
            </motion.button>
          )}
          {/* Chevron */}
          {isCollapsed ? <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" /> : <ChevronUp size={16} className="text-gray-500 dark:text-gray-400" />}
        </div>
      </div>

      {/* Collapsible Content */}
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            key="content"
            initial="closed"
            animate="open"
            exit="closed"
            variants={contentVariants}
            className="overflow-hidden" // Important for height animation
          >
            {items.length > 0 ? (
              <div className="mx-2 grid grid-cols-1 sm:grid-cols-auto-fill" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                {items.map((item, idx) => {
                  // Determine if the specific item is a destination (integration)
                  const isItemDestination = 'logo_url' in item; 
                  const itemId = isItemDestination
                    ? `destination-${(item as any).id}`
                    : `${(item as any).id}-${(item as any).type}`;
                  const isFavorite = favorites.includes(itemId);

                  return (
                    <MenuItem
                      key={itemId} // Use consistent itemId
                      item={item}
                      // Pass the correct destination status for THIS item
                      isDestination={isItemDestination} 
                      index={startIndex + idx} // This index needs adjustment based on collapsed sections
                      selectedIndex={selectedIndex}
                      onAdd={onAdd}
                      onDragStart={onDragStart}
                      onDrag={onDrag}
                      onDragEnd={onDragEnd}
                      onToggleFavorite={onToggleFavorite} // Pass down handler
                      isFavorite={isFavorite} // Pass down favorite status
                      hideFavoriteIndicator={title === "FAVORITES"} // Hide star in FAV section
                    />
                  );
                })}
              </div>
            ) : (
              // Show message if FAVORITES section is empty and expanded
              title === "FAVORITES" && (
                <div className="px-4 pb-2 text-sm text-gray-500 dark:text-gray-400 italic">
                  Click the star icon on items to add them here.
                </div>
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
